import type { MouseEvent } from 'react';
import type { EventFacts, EventOccurrence } from '../../eventsApi';
import { formatDate, formatLocalDayShort } from '../../format';
import { useOpenDetail } from '../detail-link';
import { Chip } from '../ui';
import './events.css';

/*
 * Shared event wording. Unknown stays unknown: a date-only event has "Zeit offen",
 * never 00:00; missing prices and ages are simply not stated.
 */

/** The start time as the source stated it, or null when it gave none. */
export function startTime(f: Pick<EventFacts, 'date_precision' | 'start_local'>): string | null {
  return f.date_precision === 'datetime' && f.start_local ? f.start_local : null;
}

/** Whole days from one local date to another (both YYYY-MM-DD). */
export function dayDiff(from: string, to: string): number {
  const a = Date.parse(`${from}T12:00:00Z`);
  const b = Date.parse(`${to}T12:00:00Z`);
  if (Number.isNaN(a) || Number.isNaN(b)) return 0;
  return Math.round((b - a) / 86_400_000);
}

/** "+1 Tag" / "+2 Tage" when the end falls after the start date, else ''. */
export function endDayNote(f: Pick<EventFacts, 'local_date' | 'end_local_date'>): string {
  if (!f.end_local_date) return '';
  const n = dayDiff(f.local_date, f.end_local_date);
  if (n <= 0) return '';
  return n === 1 ? '+1 Tag' : `+${n} Tage`;
}

/** "Sa., 03.10. · 20:00" from the source's previous date/time text; raw text if unparseable. */
export function formatRescheduledFrom(text: string): string {
  const m = /^(\d{4}-\d{2}-\d{2})(?:[ T](\d{2}:\d{2}))?/.exec(text.trim());
  if (!m) return text;
  return m[2] ? `${formatLocalDayShort(m[1])} · ${m[2]}` : formatLocalDayShort(m[1]);
}

export function placeLine(f: Pick<EventFacts, 'venue' | 'city'>): string {
  const parts = [f.venue, f.city].filter((x): x is string => !!x && x.trim() !== '');
  // A venue already naming its city is not repeated.
  if (parts.length === 2 && parts[0].toLocaleLowerCase('de-CH').includes(parts[1].toLocaleLowerCase('de-CH'))) return parts[0];
  return parts.join(' · ');
}

const STATUS_CHIPS: Record<string, { kind: string; label: string } | undefined> = {
  cancelled: { kind: 'danger', label: 'Abgesagt' },
  postponed: { kind: 'warn', label: 'Verschoben' },
  rescheduled: { kind: 'warn', label: 'Neues Datum' },
};

/**
 * Status badges. Event status and ticket availability are separate facts; a
 * cancelled event shows no ticket state, since "sold out" would mislead.
 */
export function EventStatusBadges({ facts }: { facts: EventFacts }) {
  const status = STATUS_CHIPS[facts.event_status];
  const soldOut = facts.ticket_status === 'sold_out' && facts.event_status !== 'cancelled';
  if (!status && !soldOut) return null;
  return (
    <>
      {status && <Chip kind={status.kind}>{status.label}</Chip>}
      {facts.event_status === 'rescheduled' && facts.rescheduled_from && (
        <span className="event-was">vorher {formatRescheduledFrom(facts.rescheduled_from)}</span>
      )}
      {soldOut && <Chip kind="info">Ausverkauft</Chip>}
    </>
  );
}

/** One occurrence in the agenda. Click, or Enter on the title, opens its details. */
export function EventCard({ occurrence: o }: { occurrence: EventOccurrence }) {
  const open = useOpenDetail();
  const versionId = o.card?.version.id ?? null;
  const time = startTime(o);
  const endNote = endDayNote(o);
  const place = placeLine(o);
  const conflicted = o.related.some((r) => r.conflicts.length > 0);

  const onSurface = (e: MouseEvent<HTMLElement>) => {
    if (!versionId) return;
    const target = e.target as HTMLElement;
    if (target.closest('a, button, input, select, textarea, summary, label, [role="button"]')) return;
    const selection = window.getSelection?.();
    if (selection && !selection.isCollapsed && selection.toString().trim() !== '') return;
    open(versionId, e.currentTarget.querySelector<HTMLElement>('.title-button'));
  };

  return (
    <article
      className={`event-row${o.event_status === 'cancelled' ? ' is-cancelled' : ''}${versionId ? '' : ' no-detail'}`}
      data-version={versionId ?? undefined}
      data-event={o.id}
      onClick={onSurface}
    >
      <div className="event-when">
        {time ? (
          <strong className="event-time">
            <time dateTime={`${o.local_date}T${time}`}>{time}</time>
          </strong>
        ) : (
          <strong className="event-time is-open">Zeit offen</strong>
        )}
        {o.end_local && (
          <span className="event-end">
            bis {o.end_local}
            {endNote && <span className="event-plus"> {endNote}</span>}
          </span>
        )}
        {o.doors_local && <span className="event-doors">Einlass {o.doors_local}</span>}
      </div>
      <div className="event-body">
        <div className="meta">
          {o.category && <span className="tag">{o.category.charAt(0).toUpperCase() + o.category.slice(1)}</span>}
          <EventStatusBadges facts={o} />
          <span className="sources">{o.source_name}</span>
        </div>
        <h3 className="story-title event-title" lang={o.language || undefined}>
          {versionId ? (
            <button type="button" className="title-button" onClick={(e) => open(versionId, e.currentTarget)}>
              {o.title}
            </button>
          ) : (
            <a href={o.canonical_url} target="_blank" rel="noopener noreferrer">
              {o.title}
            </a>
          )}
        </h3>
        {o.subtitle && <p className="event-subtitle">{o.subtitle}</p>}
        <p className="event-place">{place || <span className="muted">Ort nicht angegeben</span>}</p>
        {(o.missing_since || o.related.length > 0) && (
          <div className="meta event-notes">
            {o.missing_since && (
              <span className="tag tag-warn" title="Nicht mehr in der Liste der Quelle. Das heisst nicht, dass der Termin abgesagt ist.">
                Nicht mehr gelistet seit {formatDate(o.missing_since)}
              </span>
            )}
            {o.related.length > 0 && (
              <span>
                Auch bei {[...new Set(o.related.map((r) => r.source_name))].join(', ')}
                {conflicted && (
                  <>
                    {' '}
                    <span className="tag tag-warn">Angaben weichen ab</span>
                  </>
                )}
              </span>
            )}
          </div>
        )}
      </div>
    </article>
  );
}
